import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import HeroVideo from '../components/HeroVideo';
import ClientLogos from '../components/ClientLogos';
import { albums } from '../photographyData';

const TEASER_IMAGES = albums.flatMap((album) =>
  (album.teaserImages || [album.cover]).map((src) => ({
    src,
    alt: album.title,
    label: album.title,
    albumId: album.id,
  }))
);

export default function Home() {
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, scrollLeft: 0 });
  const didDrag = useRef(false);

  const onMouseDown = (e) => {
    setIsDragging(true);
    didDrag.current = false;
    dragStart.current = { x: e.pageX, scrollLeft: trackRef.current.scrollLeft };
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.pageX - dragStart.current.x;
    if (Math.abs(dx) > 5) didDrag.current = true;
    trackRef.current.scrollLeft = dragStart.current.scrollLeft - dx;
  };

  const stopDrag = () => setIsDragging(false);

  const scrollSlider = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 360, behavior: 'smooth' });
  };

  return (
    <>
    <Helmet>
      <title>Blackmagic Films | 6K Cinematic Videography & Photography — NJ, PA & NYC</title>
      <meta name="description" content="Premium 6K cinematic videography and photography across New Jersey, Philadelphia PA, and NYC. Music videos, wedding films, brand content & artist sessions shot on Blackmagic Cinema Cameras." />
      <link rel="canonical" href="https://www.blackmagicfilms.com/" />
      <meta property="og:title" content="Blackmagic Films | 6K Cinematic Videography — NJ, PA & NYC" />
      <meta property="og:description" content="Premium 6K cinematic video and photography. Music videos, weddings, brand content & artist sessions shot on Blackmagic Cinema Cameras. Serving NJ, Philadelphia PA, and NYC." />
      <meta property="og:url" content="https://www.blackmagicfilms.com/" />
      <meta property="og:image" content="https://www.blackmagicfilms.com/images/og-image.jpg" />
    </Helmet>
    <div className="min-h-screen bg-black text-white">
      <div className="pt-16">
        <section className="grid md:grid-cols-2 gap-0 h-auto md:h-[92vh] relative">
          <div className="flex items-center justify-center p-8">
            <motion.div
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-xl text-center md:text-left"
            >
              <div className="flex items-center justify-center md:justify-start">
                <img
                  src="/images/og-image.jpg"
                  alt="Blackmagic Films"
                  className="w-[260px] md:w-[420px] select-none"
                  draggable="false"
                  loading="eager"
                  decoding="async"
                />
              </div>

              <p className="mt-5 text-lg opacity-80">
                Cinematic music videos, B-roll studio sessions, artist tours, and premium visual storytelling for brands.
              </p>

              <div className="mt-6 flex flex-wrap gap-3 justify-center md:justify-start">
                <a
                  href="#stills"
                  className="px-5 py-3 bg-white text-black rounded-full hover:opacity-90 transition"
                >
                  Photo Work
                </a>
                <Link
                  to="/equipment"
                  className="px-5 py-3 border border-white rounded-full hover:bg-white hover:text-black transition"
                >
                  Equipment
                </Link>
              </div>
            </motion.div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-purple-700/20 via-transparent to-fuchsia-600/20 blur-3xl pointer-events-none" />
            <HeroVideo />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />
          </div>
        </section>

        <ClientLogos />

        {/* GEO: citable "About" block — speakable target, indexable text */}
        <section
          id="about-blackmagic-films"
          className="speakable max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-10 md:py-14 text-center"
        >
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">About</span>
          <h2 className="text-2xl sm:text-3xl font-black mt-2 mb-5">Blackmagic Films</h2>
          <p className="text-white/75 leading-relaxed text-base sm:text-lg">
            Blackmagic Films is a Pennsylvania-based cinematic video production company founded in 2019,
            operating out of Bucks County, PA. The company produces music videos, branded content,
            wedding films, concert coverage, and artist studio sessions for clients across Pennsylvania,
            New Jersey, Philadelphia, and New York City. Every project is captured natively on Blackmagic Cinema
            Camera 6K — a professional cinema-grade system that delivers high-resolution imagery with
            a distinctive filmic quality. Past work includes music videos for independent recording
            artists, event coverage at the Epitaph Records stage of Vans Warped Tour, branded
            content for regional businesses, and a studio session series for hip-hop and alternative
            acts across the tri-state area.
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-10 md:py-16" id="stills">
          <div className="flex items-end justify-between mb-6 gap-4">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Portfolio</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-1">Latest Stills</h2>
            </div>
            <Link
              to="/photography"
              className="shrink-0 text-sm text-white/50 hover:text-amber-400 transition-colors font-medium"
            >
              Browse all albums →
            </Link>
          </div>

          {/* Slider */}
          <div className="relative">
            <button
              onClick={() => scrollSlider(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-black/80 border border-white/20 text-white text-xl hover:border-amber-400 hover:text-amber-400 transition-colors"
              aria-label="Previous"
            >‹</button>
            <button
              onClick={() => scrollSlider(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-black/80 border border-white/20 text-white text-xl hover:border-amber-400 hover:text-amber-400 transition-colors"
              aria-label="Next"
            >›</button>

            <div
              ref={trackRef}
              className={`flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 [&::-webkit-scrollbar]:hidden select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{ scrollbarWidth: 'none' }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={stopDrag}
              onMouseLeave={stopDrag}
            >
              {TEASER_IMAGES.map((img) => (
                <Link
                  key={img.src}
                  to={`/photography?album=${img.albumId}`}
                  onClick={(e) => { if (didDrag.current) e.preventDefault(); }}
                  className="snap-start shrink-0 relative overflow-hidden rounded-xl ring-1 ring-white/10 group w-[80vw] sm:w-[360px]"
                  draggable={false}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none"
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-xs tracking-widest uppercase text-amber-400 font-medium">{img.label}</p>
                    <p className="text-white/50 text-xs mt-0.5">View album →</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/photography"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-amber-400 hover:text-amber-400 text-white/60 text-sm font-medium px-6 py-3 rounded-full transition-colors"
            >
              View All Albums
            </Link>
          </div>
        </section>

        <footer className="py-12 px-6 text-center opacity-70">
          © {new Date().getFullYear()} Blackmagic Films — Bookings: mike@blackmagicfilms.com
        </footer>
      </div>
    </div>
    </>
  );
}

